import RecoveryHelp from "../securityRecovery/RecoveryHelp";

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";

import {
  getSuperAdminUser,
  superAdminLogin,
  startSuperAdmin2FAEnrollment,
  confirmSuperAdmin2FAEnrollment,
  verifySuperAdmin2FA,
} from "./superAdminApi";

import "./SuperAdmin.css";


export default function SuperAdminLogin() {
  const navigate = useNavigate();

  const [step, setStep] = useState("password");

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const [otp, setOtp] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [verificationMethod, setVerificationMethod] =
    useState("authenticator");

  const [configUrl, setConfigUrl] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [codesSaved, setCodesSaved] = useState(false);

  const [checking, setChecking] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");


  // =====================================================
  // CHECK EXISTING VERIFIED SESSION
  // =====================================================

  useEffect(() => {
    let active = true;

    getSuperAdminUser()
      .then(() => {
        if (active) {
          navigate("/superadmin/dashboard", {
            replace: true,
          });
        }
      })
      .catch(() => {})
      .finally(() => {
        if (active) {
          setChecking(false);
        }
      });

    return () => {
      active = false;
    };
  }, [navigate]);


  // =====================================================
  // PASSWORD LOGIN
  // =====================================================

  async function submitPassword(event) {
    event.preventDefault();

    if (busy) return;

    setBusy(true);
    setError("");

    try {
      const result = await superAdminLogin(
        username.trim(),
        password
      );

      setPassword("");
      setOtp("");
      setRecoveryCode("");

      if (result.requires_enrollment) {
        setStep("enrollment");
      } else if (result.requires_2fa) {
        setStep("verification");
      } else {
        throw new Error(
          "Unexpected login response from server."
        );
      }
    } catch (err) {
      setError(err.message || "Login failed.");
    } finally {
      setBusy(false);
    }
  }


  // =====================================================
  // START AUTHENTICATOR ENROLLMENT
  // =====================================================

  async function startEnrollment() {
    if (busy) return;

    setBusy(true);
    setError("");

    try {
      const result =
        await startSuperAdmin2FAEnrollment();

      if (!result.config_url) {
        throw new Error(
          "Authenticator setup URL was not returned."
        );
      }

      setConfigUrl(result.config_url);
      setStep("enrollment-confirm");
    } catch (err) {
      setError(
        err.message || "Could not start enrollment."
      );
    } finally {
      setBusy(false);
    }
  }


  // =====================================================
  // CONFIRM AUTHENTICATOR ENROLLMENT
  // =====================================================

  async function confirmEnrollment(event) {
    event.preventDefault();

    if (busy) return;

    setBusy(true);
    setError("");

    try {
      const result =
        await confirmSuperAdmin2FAEnrollment(
          otp.trim()
        );

      if (!result.authenticated) {
        throw new Error(
          "Authenticator confirmation failed."
        );
      }

      setConfigUrl("");
      setOtp("");

      if (
        !Array.isArray(result.recovery_codes) ||
        result.recovery_codes.length === 0
      ) {
        throw new Error(
          "Recovery codes were not returned. " +
          "Contact the platform administrator."
        );
      }

      setRecoveryCodes(result.recovery_codes);
      setCodesSaved(false);
      setStep("recovery-codes");
    } catch (err) {
      setError(
        err.message || "Verification failed."
      );
    } finally {
      setBusy(false);
    }
  }


  // =====================================================
  // VERIFY EXISTING AUTHENTICATOR OR RECOVERY CODE
  // =====================================================

  async function submitVerification(event) {
    event.preventDefault();

    if (busy) return;

    setBusy(true);
    setError("");

    try {
      const result =
        await verifySuperAdmin2FA(
          verificationMethod === "authenticator"
            ? { token: otp.trim() }
            : { recovery_code: recoveryCode.trim() }
        );

      if (!result.authenticated) {
        throw new Error(
          "Two-factor authentication was not completed."
        );
      }

      setOtp("");
      setRecoveryCode("");

      navigate("/superadmin/dashboard", {
        replace: true,
      });
    } catch (err) {
      setError(
        err.message || "Verification failed."
      );
    } finally {
      setBusy(false);
    }
  }


  // =====================================================
  // RESTART LOGIN
  // =====================================================

  function restartLogin() {
    setStep("password");
    setPassword("");
    setOtp("");
    setRecoveryCode("");
    setConfigUrl("");
    setRecoveryCodes([]);
    setCodesSaved(false);
    setVerificationMethod("authenticator");
    setError("");
  }


  // =====================================================
  // RECOVERY CODE TEXT
  // =====================================================

  const recoveryCodeText = recoveryCodes.join("\n");


  // =====================================================
  // RENDER
  // =====================================================

  return (
    <main className="sa-login-page">
      <div className="sa-login-card">

        <div className="sa-logo">◆</div>

        <p className="sa-eyebrow">
          PLATFORM CONTROL
        </p>

        <h1>Super Admin</h1>

        {checking ? (
          <p className="sa-muted">
            Checking session…
          </p>
        ) : (
          <>

            {/* ========================================= */}
            {/* PASSWORD LOGIN */}
            {/* ========================================= */}

            {step === "password" && (
              <form onSubmit={submitPassword}>
                <p className="sa-muted">
                  Sign in with your platform
                  administrator account.
                </p>

                <label>
                  Username
                  <input
                    autoComplete="username"
                    required
                    value={username}
                    onChange={(event) =>
                      setUsername(event.target.value)
                    }
                  />
                </label>

                <label>
                  Password
                  <input
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(event) =>
                      setPassword(event.target.value)
                    }
                  />
                </label>

                {error && (
                  <p className="sa-error" role="alert">
                    {error}
                  </p>
                )}

                <button type="submit" disabled={busy}>
                  {busy
                    ? "Checking credentials…"
                    : "Continue securely"}
                </button>
              </form>
            )}


            {/* ========================================= */}
            {/* FIRST-TIME ENROLLMENT */}
            {/* ========================================= */}

            {step === "enrollment" && (
              <div>
                <h2>Set up two-factor authentication</h2>

                <p className="sa-muted">
                  Your administrator account requires
                  an authenticator app.
                </p>

                <p className="sa-muted">
                  Install Google Authenticator,
                  Microsoft Authenticator, or another
                  compatible authenticator app.
                </p>

                {error && (
                  <p className="sa-error" role="alert">
                    {error}
                  </p>
                )}

                <button
                  type="button"
                  disabled={busy}
                  onClick={startEnrollment}
                >
                  {busy
                    ? "Preparing setup…"
                    : "Set up authenticator"}
                </button>

                <button
                  type="button"
                  className="sa-secondary-button"
                  onClick={restartLogin}
                  disabled={busy}
                >
                  Back to login
                </button>
              </div>
            )}


            {/* ========================================= */}
            {/* ENROLLMENT QR + CONFIRMATION */}
            {/* ========================================= */}

            {step === "enrollment-confirm" && (
              <form onSubmit={confirmEnrollment}>
                <h2>Scan authenticator QR code</h2>

                <p className="sa-muted">
                  Open your authenticator app,
                  choose Add Account, and scan
                  this QR code.
                </p>

                {configUrl && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      padding: "20px",
                      margin: "16px 0",
                      background: "#ffffff",
                      borderRadius: "12px",
                    }}
                  >
                    <QRCodeSVG
                      value={configUrl}
                      size={200}
                      level="M"
                    />
                  </div>
                )}

                <p className="sa-muted">
                  Enter the six-digit code displayed
                  by your authenticator app.
                </p>

                <label>
                  Authenticator code
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    required
                    value={otp}
                    onChange={(event) =>
                      setOtp(
                        event.target.value.replace(
                          /\D/g,
                          ""
                        )
                      )
                    }
                  />
                </label>

                {error && (
                  <p className="sa-error" role="alert">
                    {error}
                  </p>
                )}

                <button type="submit" disabled={busy}>
                  {busy
                    ? "Verifying…"
                    : "Confirm authenticator"}
                </button>

                <button
                  type="button"
                  className="sa-secondary-button"
                  onClick={restartLogin}
                  disabled={busy}
                >
                  Restart login
                </button>
              </form>
            )}


            {/* ========================================= */}
            {/* EXISTING ACCOUNT OTP VERIFICATION */}
            {/* ========================================= */}

            {step === "verification" && (
              <form onSubmit={submitVerification}>
                <h2>Two-factor verification</h2>

                <p className="sa-muted">
                  Your password has been verified.
                  Complete the second step to access
                  your dashboard.
                </p>

                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    marginBottom: "16px",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setVerificationMethod(
                        "authenticator"
                      );
                      setError("");
                    }}
                    aria-pressed={
                      verificationMethod ===
                      "authenticator"
                    }
                  >
                    Authenticator
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setVerificationMethod(
                        "recovery"
                      );
                      setError("");
                    }}
                    aria-pressed={
                      verificationMethod === "recovery"
                    }
                  >
                    Recovery code
                  </button>
                </div>

                {verificationMethod ===
                "authenticator" ? (
                  <label>
                    Six-digit authenticator code
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      pattern="[0-9]{6}"
                      maxLength={6}
                      required
                      value={otp}
                      onChange={(event) =>
                        setOtp(
                          event.target.value.replace(
                            /\D/g,
                            ""
                          )
                        )
                      }
                    />
                  </label>
                ) : (
                  <label>
                    Recovery code
                    <input
                      type="text"
                      autoComplete="off"
                      maxLength={8}
                      minLength={8}
                      required
                      value={recoveryCode}
                      onChange={(event) =>
                        setRecoveryCode(
                          event.target.value
                        )
                      }
                    />
                  </label>
                )}

                {error && (
                  <p className="sa-error" role="alert">
                    {error}
                  </p>
                )}

                <button type="submit" disabled={busy}>
                  {busy
                    ? "Verifying…"
                    : "Verify and sign in"}
                </button>

                <button
                  type="button"
                  className="sa-secondary-button"
                  onClick={restartLogin}
                  disabled={busy}
                >
                  Restart login
                </button>
              </form>
            )}


<RecoveryHelp audience="platform" onUseCode={() => { setVerificationMethod("recovery"); setError(""); }} />
                        {/* ========================================= */}
            {/* SAVE RECOVERY CODES */}
            {/* ========================================= */}

            {step === "recovery-codes" && (
              <div>
                <h2>Save your recovery codes</h2>

                <p className="sa-muted">
                  Your authenticator is configured.
                  These single-use recovery codes
                  can help you sign in if you lose
                  access to your authenticator app.
                </p>

                <p className="sa-error" role="alert">
                  Save these codes somewhere secure.
                  They will not be shown again.
                </p>

                <pre
                  style={{
                    padding: "16px",
                    background: "#f3f4f6",
                    color: "#111827",
                    borderRadius: "10px",
                    textAlign: "center",
                    lineHeight: "1.9",
                    overflowX: "auto",
                  }}
                >
                  {recoveryCodeText}
                </pre>

                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(
                        recoveryCodeText
                      );
                    } catch {
                      setError(
                        "Copy failed. Save the codes manually."
                      );
                    }
                  }}
                >
                  Copy recovery codes
                </button>

                {error && (
                  <p className="sa-error" role="alert">
                    {error}
                  </p>
                )}

                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginTop: "16px",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={codesSaved}
                    onChange={(event) =>
                      setCodesSaved(event.target.checked)
                    }
                  />
                  I have saved my recovery codes securely.
                </label>

                <button
                  type="button"
                  disabled={!codesSaved}
                  onClick={() => {
                    setRecoveryCodes([]);
                    navigate(
                      "/superadmin/dashboard",
                      { replace: true }
                    );
                  }}
                >
                  Continue to dashboard
                </button>
              </div>
            )}

          </>
        )}

      </div>
    </main>
  );
}